import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DASHBOARD_TABS } from '../constants';
import { useDashboardData } from './DashboardDataContext';

const DashboardUIContext = createContext(null);

export const DashboardUIProvider = ({ children }) => {
  const location = useLocation();
  const { fetchDashboards, fetchTemplates } = useDashboardData();
  const activeTab = location.pathname.includes('/templates')
    ? DASHBOARD_TABS.TEMPLATES
    : DASHBOARD_TABS.CREATED;

  const [state, setState] = useState({
    filters: { search: '', state: '', sectionId: '', categoryId: '' },
    templateFilters: { search: '', sectionId: '', categoryId: '' },
  });

  const setFilters = useCallback((newFilters) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, ...newFilters },
    }));
  }, []);

  const setTemplateFilters = useCallback((newFilters) => {
    setState((prev) => ({
      ...prev,
      templateFilters: { ...prev.templateFilters, ...newFilters },
    }));
  }, []);

  useEffect(() => {
    const isListRoute =
      !location.pathname.includes('/new') &&
      !location.pathname.includes('/edit') &&
      !location.pathname.includes('/templates');

    if (activeTab === DASHBOARD_TABS.CREATED && isListRoute) {
      fetchDashboards(state.filters);
    }
  }, [activeTab, fetchDashboards, location.pathname, state.filters]);

  useEffect(() => {
    const isListRoute = !location.pathname.includes('/new') && !location.pathname.includes('/edit');
    if (activeTab === DASHBOARD_TABS.TEMPLATES && isListRoute) {
      fetchTemplates(state.templateFilters);
    }
  }, [activeTab, fetchTemplates, location.pathname, state.templateFilters]);

  const value = useMemo(
    () => ({
      activeTab,
      filters: state.filters,
      templateFilters: state.templateFilters,
      setFilters,
      setTemplateFilters,
    }),
    [activeTab, state.filters, state.templateFilters, setFilters, setTemplateFilters],
  );

  return <DashboardUIContext.Provider value={value}>{children}</DashboardUIContext.Provider>;
};

export const useDashboardUI = () => {
  const context = useContext(DashboardUIContext);
  if (!context) {
    throw new Error('useDashboardUI must be used within DashboardUIProvider');
  }
  return context;
};
